import React, { createContext, useContext, useState, useCallback } from "react";
import Dialog from "./Dialog";
import { getInfoDialogProps } from "./types/InfoDialog";
import { getWarningDialogProps } from "./types/WarningDialog";
import { getConfirmDialogProps } from "./types/ConfirmDialog";
import { getSuccessDialogProps } from "./types/SuccessDialog";
import { getErrorDialogProps } from "./types/ErrorDialog";

const DialogContext = createContext();

export const DialogProvider = ({ children }) => {
  const [dialogs, setDialogs] = useState([]);

  const showDialog = useCallback((dialogProps) => {
    const id = Date.now() + Math.random();

    const dialog = {
      id,
      ...dialogProps,
      isOpen: true,
      onClose: () => {
        setDialogs((prev) =>
          prev.map((d) => (d.id === id ? { ...d, isOpen: false } : d))
        );

        setTimeout(() => {
          setDialogs((prev) => prev.filter((d) => d.id !== id));
        }, 300);

        if (dialogProps.onClose) {
          dialogProps.onClose();
        }
      },
    };

    setDialogs((prev) => [...prev, dialog]);
    return id;
  }, []);

  const hideDialog = useCallback((id) => {
    setDialogs((prev) =>
      prev.map((d) => (d.id === id ? { ...d, isOpen: false } : d))
    );

    setTimeout(() => {
      setDialogs((prev) => prev.filter((d) => d.id !== id));
    }, 300);
  }, []);

  const hideAllDialogs = useCallback(() => {
    setDialogs((prev) => prev.map((d) => ({ ...d, isOpen: false })));

    setTimeout(() => {
      setDialogs([]);
    }, 300);
  }, []);

  React.useLayoutEffect(() => {
    window.__dialogShow = showDialog;
    window.__dialogHide = hideDialog;
    window.__dialogHideAll = hideAllDialogs;

    return () => {
      delete window.__dialogShow;
      delete window.__dialogHide;
      delete window.__dialogHideAll;
    };
  }, [showDialog, hideDialog, hideAllDialogs]);

  const value = {
    showDialog,
    hideDialog,
    hideAllDialogs,
    dialogs,
  };

  return (
    <DialogContext.Provider value={value}>
      {children}

      {dialogs.map((dialog) => (
        <Dialog key={dialog.id} {...dialog} />
      ))}
    </DialogContext.Provider>
  );
};

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error("useDialog must be used within a DialogProvider");
  }
  return context;
};

export const dialog = {
  show: (props) => {
    if (typeof window !== "undefined" && window.__dialogShow) {
      return window.__dialogShow(props);
    }
    return null;
  },

  hide: (id) => {
    if (typeof window !== "undefined" && window.__dialogHide) {
      window.__dialogHide(id);
    }
  },

  hideAll: () => {
    if (typeof window !== "undefined" && window.__dialogHideAll) {
      window.__dialogHideAll();
    }
  },

  info: (props) => {
    return dialog.show(getInfoDialogProps(props));
  },

  warning: (props) => {
    return dialog.show(getWarningDialogProps(props));
  },

  confirm: (props) => {
    return dialog.show(getConfirmDialogProps(props));
  },

  success: (props) => {
    return dialog.show(getSuccessDialogProps(props));
  },

  error: (props) => {
    return dialog.show(getErrorDialogProps(props));
  },
};

export default DialogProvider;
